import { useCallback, useMemo } from "react";
import { useLazyQuery } from "@apollo/client/react";

import FlashContext from "components/FlashMessages/FlashContext";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import { canDeleteVersion } from "shared/Templates/access/canDeleteVersion";
import useTypedContext from "hooks/useTypedContext";
import { TemplateVersion, BlueprintState } from "types/generated";
import TemplateFeatureGateTooltip from "views/Account/TemplatesWorkbench/FeatureGate/Tooltip";
import { TemplateActions } from "views/Account/TemplatesWorkbench/FeatureGate/types";
import { GET_TEMPLATE_VERSION } from "views/Account/TemplateVersion/gql";
import { showVersionDetailsDrawer } from "views/Account/TemplateVersion/DetailsDrawer";

import { TemplateContext } from "../../../Context";
import { showCreateVersionDrawer } from "../../CreateVersionDrawer";
import { showDeprecateConfirmation } from "../../DeprecateConfirmation";
import { showDeleteConfirmation } from "../../DeleteConfirmation";

const TemplatesTableRowActionsCell = ({
  item: version,
  ...cellProps
}: TableSimpleCellProps<TemplateVersion>) => {
  const templateContext = useTypedContext(TemplateContext);
  const { onError } = useTypedContext(FlashContext);
  const canDeleteVersionResult = canDeleteVersion(templateContext.template, version.deploymentsCount);

  const [getTemplateVersion] = useLazyQuery(GET_TEMPLATE_VERSION);

  const showDeprecateAction =
    version.state === BlueprintState.Published && !version.deprecatedAt;

  const handleOpenCloneDrawer = useCallback(async () => {
    try {
      const { data, error } = await getTemplateVersion({
        variables: {
          id: templateContext.template.id,
          versionId: version.id,
        },
      });

      if (error) {
        onError(error);
        return;
      }

      if (data?.template?.templateVersion) {
        showCreateVersionDrawer({
          version: data.template.templateVersion,
          templateId: templateContext.template.id,
          isCloneMode: true,
        });
      }
    } catch (error) {
      onError(error);
    }
  }, [version.id, templateContext.template.id, getTemplateVersion, onError]);

  const handleOpenDetailsDrawer = useCallback(() => {
    showVersionDetailsDrawer({ item: version });
  }, [version]);

  const menuItems = useMemo(() => {
    const items = [];

    items.push(
      <DropdownMenuItem key="details" onAction={handleOpenDetailsDrawer}>
        Details
      </DropdownMenuItem>
    );

    items.push(
      <TemplateFeatureGateTooltip
        key="duplicate"
        on={(props) => (
          <DropdownMenuItem {...props} onAction={handleOpenCloneDrawer}>
            Duplicate
          </DropdownMenuItem>
        )}
        template={templateContext.template}
        action={TemplateActions.EditTemplate}
        tooltipPlacement="left"
      />
    );

    if (showDeprecateAction) {
      items.push(
        <TemplateFeatureGateTooltip
          key="deprecate"
          on={(props) => (
            <DropdownMenuItem
              {...props}
              danger
              onAction={() =>
                showDeprecateConfirmation({
                  id: version.id,
                  versionLabel: `${version.name} - ${version.version}`,
                })
              }
            >
              Deprecate version
            </DropdownMenuItem>
          )}
          template={templateContext.template}
          action={TemplateActions.EditTemplate}
          tooltipPlacement="left"
        />
      );
    }

    if (canDeleteVersionResult.result || canDeleteVersionResult.message) {
      items.push(
        <DropdownMenuItem
          key="delete"
          onAction={() =>
            showDeleteConfirmation({
              id: version.id,
              version: `${version.name} - ${version.version}`,
            })
          }
          danger
          isDisabled={!canDeleteVersionResult.result}
          tooltip={canDeleteVersionResult.message}
          tooltipPlacement="left"
        >
          Delete
        </DropdownMenuItem>
      );
    }

    return items;
  }, [
    version,
    canDeleteVersionResult,
    templateContext.template,
    handleOpenCloneDrawer,
    handleOpenDetailsDrawer,
    showDeprecateAction,
  ]);

  return (
    <TableSimpleCell {...cellProps}>
      {menuItems.length > 0 && (
        <DropdownMenuEllipsis tooltip="Version actions">{menuItems}</DropdownMenuEllipsis>
      )}
    </TableSimpleCell>
  );
};

export default TemplatesTableRowActionsCell;
